import * as React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import Helmet from 'react-helmet-async';
import { resetCSSStyle } from './util/css/reset-css-styles.util';
import { GlobalErrorsHolder } from './component/global-errors-holder/global-errors-holder';
import { renderRoutes } from './util/route/render-routes';
import * as Themes from './theme';
import { invariant } from './util/debug/invariant.util';
import * as moment from 'moment';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from './interface/component/injected-props.interface';
import { ColorEnum } from './enum/color.enum';
import { addOpacity } from './util/style/add-opacity.util';
import { getFontStyle } from './util/style/get-font-style.util';
import { getColorHexValue } from './util/style/get-color-hex-value.util';
import { action, autorun, observable } from 'mobx';
import { CookiePolicy } from './component/cookie-policy/cookie-policy';
import Loader from './component/loaders/loader';
import { IButtonTheme } from './interface/button-theme.interface';
import { buttonThemes } from './component/cms-button/button-themes';
import { LOADING_SHOW_TIMEOUT } from './constants';
import { isStringEmpty } from './util/string/is-string-empty.util';
import {CustomFontsInclusions} from './util/block/custom-fonts-inclusions.util';
import {IeWarning} from './component/ie-warning/ie-warning';
// import {OneTrustCookiePolicy} from './component/cookie-policy/onetrust-policy';
import {CookieInformationPolicy} from './component/cookie-policy/cookie-information-policy';

const themeKeys = Object.keys(Themes);
const MainContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  ${(props: { loading: boolean }) => props.loading ? 'pointer-events: none;' : ''}
`;

const VersionWrapper = styled.div`
  font-size: 11px;
  z-index: 99999999999999999999;
  padding: 3px;
  opacity: 0.7;
  display: ${(props: { debug }) => props.debug ? 'block' : 'none'};
  position: fixed;
  right: 10px;
  bottom: 5px;
  pointer-events: none;
  color: #F5F5F5;
  background-color: #303030;
`;

const ThreeDotsWrapper = styled.div`
  width: 100%;
  height: 100%;
  display:flex;
  justify-content: center;
  align-items: center;
  color: ${ColorEnum.AppChrome};
  position: fixed;
  z-index: 3000;
  left:0;
  top:0;
  background: ${(props) => addOpacity(ColorEnum.PearlGray(props), 0.5)};
`;

declare global {
  interface Window {
    nonce: string;
  }
}

const nonce = window.nonce;

@inject('state')
@observer
export class RoutesWrapper extends React.Component<{ route: any } & IInjectedProps> {
  @observable mounted: boolean = false;
  @observable isLoading: boolean = false;
  timeout;
  clearTimeoutTimeout;
  listener;

  componentWillMount() {
    moment.locale(this.props.state.configuration.locale);
  }

  clearTimeout = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  reset = () => {
    if (this.clearTimeoutTimeout) {
      clearTimeout(this.clearTimeoutTimeout);
      this.clearTimeoutTimeout = null;
    }
  }

  @action
  componentDidMount() {
    this.mounted = true;
    this.listener = autorun(() => {
      this.reset();
      if (this.props.state.isLoading || this.props.state.loadingScriptCount > 0) {
        if (!this.timeout) {
          this.timeout = setTimeout(action(() => {
            this.isLoading = true;
          }), LOADING_SHOW_TIMEOUT);
        }
      } else {
        this.clearTimeoutTimeout = setTimeout(action(() => {
          this.clearTimeout();
          if (this.isLoading) {
            this.isLoading = false;
          }
        }), 100);
      }
    });
  }

  componentWillUnmount() {
    if (this.listener) {
      this.listener();
      this.listener = null;
    }
    this.clearTimeout();
  }

  render() {
    const theme = {};

    for (const themeKey of themeKeys) {
      invariant(Boolean(Themes[ themeKey ].themeNamespace && Themes[ themeKey ].theme), 'You must export a theme and a themeNamespace object for each theme you create!', Themes[ themeKey ]);
      theme[ Themes[ themeKey ].themeNamespace ] = Themes[ themeKey ].theme(this.props);
    }
    const allColors = this.props.state.colors.getAllRawColors();
    let classes = '';
    classes += allColors.map((color) => `.color-${color.id} {color: ${getColorHexValue(color, allColors)};}`).join('\n\r');
    classes += '\n\r\n\r' + allColors.map((color) => `.bgcolor-${color.id} {background-color: ${getColorHexValue(color, allColors)};}`).join('\n\r');
    classes += '\n\r\n\r' + this.props.state.fonts.getAllRawFonts().map((font) => `.font-${font.id} {${getFontStyle(font, allColors).css}}`).join('\n\r');
    const predefinedFonts = this.props.state.fonts.getAllRawFonts().filter((f) => f.predefined).map(f => f.fontFamily);
    const buttonTheme: IButtonTheme = buttonThemes[ this.props.state.configuration.settings.buttonThemeId ];
    invariant(Boolean(buttonTheme), 'No button theme for given id', this.props.state.configuration.settings.buttonThemeId);
    return (
      <>
        <CustomFontsInclusions fontsList={predefinedFonts} />
        {/* <OneTrustCookiePolicy /> */}
        <CookieInformationPolicy />
        <Helmet>
          <style>
            {resetCSSStyle(this.props.state)}
          </style>
          <style>
            {`
                        ${this.props.state.configuration.isMobile ? `div {
                            cursor: pointer;
                        }` : ''}
                        #content > div > div > div {
                        background: ${this.props.state.colors.Transparent} !important;
                        color: ${this.props.state.colors.ContentColor} !important;
                    }`}
          </style>
          <style>
            {classes}
          </style>
          <meta
            name="viewport"
            content="width=device-width,initial-scale=1.0"
          />
          {isStringEmpty(this.props.state.adminSettings.tagManagerId) ? undefined : <script nonce={nonce}>
              {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;j.setAttribute('nonce', '${nonce}');f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${this.props.state.adminSettings.tagManagerId}');
            `}
          </script>}
        </Helmet>
        <ThemeProvider
          theme={{
            _ip_colors: this.props.state.configuration.settings.colors.map((color) => {
              return Object.assign({}, color);
            }),
            _ip_fonts: this.props.state.configuration.settings.fonts.map((font) => {
              return Object.assign({}, font);
            }),
            _window_size: this.mounted ? Object.assign({}, this.props.state.windowSize) : {},
            _button: buttonTheme
          }}
        >
          <ThemeProvider theme={theme}>
            <MainContentWrapper loading={this.isLoading}>
              <GlobalErrorsHolder/>
              <IeWarning />
              {renderRoutes(this.props.route.routes)}
              {this.props.state.adminSettings.enableIntelliplanCookiePolicy ? <CookiePolicy/> : undefined}
              {this.isLoading ?
                <ThreeDotsWrapper>
                  <Loader
                    width={200}
                    height={200}
                  />
                </ThreeDotsWrapper>
                : undefined
              }
              {isStringEmpty(this.props.state.adminSettings.tagManagerId) ? undefined :
                <noscript
                    dangerouslySetInnerHTML={{__html: `<iframe
                      src="https://www.googletagmanager.com/ns.html?id=${this.props.state.adminSettings.tagManagerId}"
                      height="0"
                      width="0"
                      style="display: none; visibility: hidden"
                    />`}}
                />}
            </MainContentWrapper>
          </ThemeProvider>
        </ThemeProvider>
        {this.mounted && <VersionWrapper debug={this.props.state.configuration.debug}>
          Version: {this.props.state.configuration.currentVersion}
        </VersionWrapper>}
      </>
    );
  }
}
